import { useAuth0 } from '@auth0/auth0-react';
import {
  Actions,
  Alert,
  Button,
  Divider,
  Heading,
  IconChevron,
  List,
  Loader,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';

import { BasePage } from '#src/components/BasePage/BasePage.tsx';
import type { Breadcrumb } from '#src/components/Breadcrumbs/Breadcrumbs.tsx';
import { ErrorBoundary } from '#src/components/ErrorBoundary/ErrorBoundary.tsx';
import { useLogin } from '#src/hooks/auth.tsx';
import { IntegrationHealthSummarySection } from '../observability/IntegrationHealthSummarySection/IntegrationHealthSummarySection.tsx';
import { SystemStatus } from './components/SystemStatus/SystemStatus.tsx';

const quickActions = [
  {
    href: '/manage/hirers',
    title: 'Hirer relationships',
    description:
      'View hirer accounts and their relationship with your platform',
  },
  {
    href: '/manage/webhooks',
    title: 'Webhook subscriptions',
    description: 'Manage and view the health of your subscriptions',
  },
  {
    href: '/manage/events',
    title: 'Polling',
    description:
      'View and backfill events for troubleshooting or disaster recovery',
  },
  {
    href: '/manage/job-ads',
    title: 'Job posting Job Ads',
    description: 'Query and preview job ads posted by your hirers',
  },
];

const developerToolkit = [
  {
    href: '/manage/credentials',
    title: 'Client credentials',
    description: 'Manage your live and Playground credentials for the SEEK API',
  },
  {
    href: '/manage/graphql-explorer',
    title: 'GraphQL Explorer',
    description: 'Build and test queries in real-time',
  },
  {
    href: '/manage/webhook-playground',
    title: 'Webhook playground',
    description: 'Configure and send playground webhook events for testing',
  },
  {
    href: '/manage/job-posting',
    title: 'Job posting features',
    description: 'Interactively explore features of the Job Posting use case',
  },
];

const AuthenticatedDashboardHomePageContents = () => (
  <Stack space="xlarge">
    <SystemStatus />

    <Stack space="medium">
      <ErrorBoundary
        fallback={
          <Alert tone="caution">
            <Text>
              Unable to render the Integration Health Summary. Please try again
              later.
            </Text>
          </Alert>
        }
      >
        <IntegrationHealthSummarySection />
      </ErrorBoundary>

      <Text>
        <TextLink
          href="/manage/integration-health"
          iconPosition="trailing"
          icon={<IconChevron direction="right" />}
        >
          View your integration health
        </TextLink>
      </Text>
    </Stack>

    <Divider />

    <Stack space="small">
      <Text weight="strong">Quick Actions</Text>
      <List>
        {quickActions.map((action) => (
          <Text key={action.href}>
            <TextLink href={action.href}>{action.title}</TextLink>:{' '}
            {action.description}
          </Text>
        ))}
      </List>
    </Stack>

    <Stack space="small">
      <Text weight="strong">Developer Toolkit</Text>
      <List>
        {developerToolkit.map((action) => (
          <Text key={action.href}>
            <TextLink href={action.href}>{action.title}</TextLink>:{' '}
            {action.description}
          </Text>
        ))}
      </List>
    </Stack>
  </Stack>
);

const UnauthenticatedDashboardHomePageContents = () => {
  const { login } = useLogin();

  return (
    <Stack space="xlarge">
      <SystemStatus />

      <Stack space="medium">
        <Heading level="3">Welcome to our developer dashboard</Heading>

        <Text>
          This is your all-in-one control centre for interactively monitoring
          and troubleshooting your SEEK API integration.
        </Text>
      </Stack>

      <Stack space="medium">
        <Heading level="4">How to get started</Heading>

        <Stack space="small">
          <Text>
            To access the tools below, you’ll need permission from SEEK.
          </Text>

          <List space="small">
            <Text>
              Contact us and nominate at least one admin contact’s email address
            </Text>
            <Text>
              Once access is granted, you’ll be able to sign in and use the
              tools
            </Text>
          </List>
        </Stack>
      </Stack>

      <Stack space="medium">
        <Text weight="strong">Already have access?</Text>

        <Actions>
          <Button onClick={() => login({})} tone="formAccent" size="small">
            Sign in to continue
          </Button>
        </Actions>
      </Stack>

      <Divider />

      <Heading level="3">Tools available after signing in</Heading>

      <Stack space="small">
        <Text weight="strong">Your integration health</Text>
        <Text>
          Monitor your system’s performance with real-time metrics and deep dive
          into queries to troubleshoot issues.
        </Text>
      </Stack>

      <Stack space="small">
        <Text weight="strong">Quick Actions</Text>
        <List>
          {quickActions.map((action) => (
            <Text key={action.href}>
              {action.title}: {action.description}
            </Text>
          ))}
        </List>
      </Stack>

      <Stack space="small">
        <Text weight="strong">Developer Toolkit</Text>
        <List>
          {developerToolkit.map((action) => (
            <Text key={action.href}>
              {action.title}: {action.description}
            </Text>
          ))}
        </List>
      </Stack>
    </Stack>
  );
};

const DashboardHomePageContents = () => {
  const { isAuthenticated, isLoading } = useAuth0();

  if (isLoading) {
    return <Loader delayVisibility />;
  }

  return isAuthenticated ? (
    <AuthenticatedDashboardHomePageContents />
  ) : (
    <UnauthenticatedDashboardHomePageContents />
  );
};

export const DashboardHomePage = ({
  breadcrumbs,
}: {
  breadcrumbs: Breadcrumb[];
}) => (
  <BasePage title="Developer Dashboard" breadcrumbs={breadcrumbs}>
    <DashboardHomePageContents />
  </BasePage>
);
